import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { api, patchApi, smartApi, type CatchUpView, type ConversationView, type MessagesInbox, type PatchView, type PresenceList } from '../../api/client';
import { Avatar } from '../../components/messages/Avatar';
import { CatchUpPanel, SmartContext, SmartSide, TextStateMark } from '../../components/messages/Smart';
import { TeamMark, teamOrder } from '../../components/messages/TeamTexts';
import { InstallOffer } from '../../components/Notifications';
import { PhonePageHead } from '../../components/PhoneApp';
import { Button, Empty, Notice, Sheet } from '../../components/ui';
import { plural, shortWhen } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { useMessagesLive } from '../../lib/messages';
import { plainPreview } from '../../lib/richtext';

/**
 * The phone app's Home and DMs tabs (components/PhoneApp), Slack's shape in Stitch's look: shortcuts
 * across the top, then what's unread (direct messages, mentions, everything else), then channels,
 * direct messages and texts; a button to start something new. Opening a conversation gives it the
 * whole screen (Messages.tsx); its back button returns to the list it came from.
 */

// --- back to the list a conversation was opened from ------------------------------------------------

let backTo: string | null = null;
/** Where a conversation's back button goes on a phone: the list it was opened from (Home, DMs, Activity). */
export const listToGoBackTo = (fallback: string): string => backTo ?? fallback;
/** A list a conversation can be opened from: its back button comes here. */
export function useRememberList(): void {
  const { pathname, search } = useLocation();
  useEffect(() => {
    backTo = pathname + search;
  }, [pathname, search]);
}

export interface PhoneInboxProps {
  domain: string;
  /** /t/:tenant/messages */
  base: string;
  company: string;
  myId: string;
  inbox: MessagesInbox;
  presence: PresenceList | undefined;
  admin: boolean;
  showTexts: boolean;
  /** As the inbox shows them (Done and snoozed ones left out). */
  channels: ConversationView[];
  dms: ConversationView[];
  texts: ConversationView[];
  /** Texts you archived (1.12.30): out of the list until that number texts again. */
  archivedTexts: ConversationView[];
  /** You get texts at a company number, so you can text someone from it (1.12.30). */
  canStartText: boolean;
  onDialog: (d: 'channel' | 'dm' | 'browse' | 'search' | 'text') => void;
  onChanged: () => void;
}

const muted = (c: ConversationView) => (c.notify ?? c.notifyDefault) === 'muted';
const tenantOf = (base: string) => base.replace(/\/messages$/, '');

/** Sections someone closed on their phone stay closed (this device only). */
function useClosed(id: string): [boolean, () => void] {
  const key = `stitch.phome.closed.${id}`;
  const [closed, setClosed] = useState(() => {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setClosed((was) => {
      try {
        if (was) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, '1');
      } catch {
        // Private browsing: it just isn't remembered.
      }
      return !was;
    });
  return [closed, toggle];
}

function ConversationIcon({ c, myId, presence }: { c: ConversationView; myId: string; presence: PresenceList | undefined }) {
  const others = c.people.filter((p) => p.id !== myId);
  if (c.kind === 'channel') return <Icon name={c.access === 'private' ? 'lock' : 'hash'} size={19} strokeWidth={2.1} />;
  if (c.kind === 'text') return <Icon name="phone" size={18} />;
  if (others.length === 1) return <Avatar id={others[0]!.id} name={others[0]!.name} small presence={presence?.people[others[0]!.id]} />;
  return <Icon name="users" size={18} />;
}

/** A conversation in a Home section: unread ones in bold; a number for direct messages, texts and mentions. */
function Row({ base, c, myId, presence, count, countLabel }: { base: string; c: ConversationView; myId: string; presence: PresenceList | undefined; count?: number; countLabel?: string }) {
  const quiet = muted(c);
  return (
    <Link to={`${base}/${c.id}`} className={`phome-row${c.unread > 0 && !quiet ? ' unread' : ''}${quiet ? ' muted' : ''}`}>
      <span className="phome-row-icon" aria-hidden="true">
        <ConversationIcon c={c} myId={myId} presence={presence} />
      </span>
      <span className="phome-row-title">{c.title || 'Just you'}</span>
      {c.kind === 'text' && <TextStateMark id={c.id} /> /* smart messaging */}
      {c.kind === 'text' && c.team && <TeamMark t={c.team} /> /* 1.12.32 */}
      {count ? (
        <span className="phome-badge">
          {count > 99 ? '99+' : count}
          <span className="sr-only"> {countLabel ?? 'unread'}</span>
        </span>
      ) : c.unread > 0 && !quiet ? (
        <span className="sr-only">, unread</span>
      ) : null}
    </Link>
  );
}

function Section({ id, title, to, icon, collapsible, children }: { id: string; title: string; to?: string; icon?: IconName; collapsible?: boolean; children: ReactNode }) {
  const [closed, toggle] = useClosed(id);
  const shut = Boolean(collapsible && closed);
  return (
    <section className="phome-section" aria-labelledby={`phome-${id}`}>
      <div className="phome-section-head">
        <h2 id={`phome-${id}`} className="phome-section-title">
          {icon && <Icon name={icon} size={18} />}
          {to ? (
            <Link to={to}>
              {title}
              <Icon name="chevRight" size={16} strokeWidth={2.4} />
            </Link>
          ) : (
            <span>{title}</span>
          )}
        </h2>
        {collapsible && (
          <button type="button" className="phome-collapse" aria-expanded={!shut} aria-label={shut ? `Show ${title}` : `Hide ${title}`} onClick={toggle}>
            <Icon name="chev" size={18} strokeWidth={2.2} />
          </button>
        )}
      </div>
      {!shut && <div className="phome-rows">{children}</div>}
    </section>
  );
}

function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="phome-row add" onClick={onClick}>
      <span className="phome-row-icon" aria-hidden="true">
        <Icon name="plus" size={18} strokeWidth={2.2} />
      </span>
      <span className="phome-row-title">{label}</span>
    </button>
  );
}

/** A shortcut across the top of Home (Slack's Catch Up, Huddles…). */
function QuickCard({ icon, title, sub, to, onClick, hot }: { icon: IconName; title: string; sub: string; to?: string; onClick?: () => void; hot?: boolean }) {
  const body = (
    <>
      <span className="phome-card-icon" aria-hidden="true">
        <Icon name={icon} size={20} strokeWidth={2} />
        {hot && <span className="phome-card-dot" />}
      </span>
      <strong>{title}</strong>
      <span className={hot ? 'hot' : 'muted'}>{sub}</span>
    </>
  );
  return to ? (
    <Link to={to} className="phome-card" role="listitem">
      {body}
    </Link>
  ) : (
    <button type="button" className="phome-card" role="listitem" onClick={onClick}>
      {body}
    </button>
  );
}

/** The Patches going on in the company now (quick calls in a conversation), kept up to date. */
function useLivePatches(domain: string): { enabled: boolean; patches: PatchView[] } | null {
  const [live, setLive] = useState<{
    enabled: boolean;
    patches: PatchView[];
  } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const load = useMemo(
    () => () =>
      patchApi
        .live(domain)
        .then(setLive)
        .catch(() => setLive((was) => was ?? { enabled: false, patches: [] })),
    [domain]
  );
  useEffect(() => {
    void load();
    return () => window.clearTimeout(timer.current);
  }, [load]);
  useMessagesLive((e) => {
    if (e.type !== 'patch' && e.type !== 'reset') return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void load(), 400);
  });
  return live;
}

/** New voicemail in your own mailbox, for Home's shortcut (people with a phone). */
function useNewVoicemail(domain: string, on: boolean): number | null {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    if (!on) return;
    let alive = true;
    api
      .voicemail(domain)
      .then((b) => alive && setN(b.newCount))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [domain, on]);
  return n;
}

/** Catch up: each conversation with something unread, and a summary of it on request (AI, for you only). */
function CatchUpSheet({ open, onClose, domain, base, rows }: { open: boolean; onClose: () => void; domain: string; base: string; rows: ConversationView[] }) {
  const [done, setDone] = useState<Record<string, CatchUpView>>({});
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  const run = async (c: ConversationView) => {
    setBusy(c.id);
    setProblem('');
    try {
      const r = await smartApi.catchUp(domain, c.id, c.lastReadSeq);
      setDone((d) => ({ ...d, [c.id]: r.catchUp }));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That didn’t work. Try again.');
    } finally {
      setBusy('');
    }
  };
  return (
    <Sheet open={open} onClose={onClose} title="Catch up" subtitle="What you haven’t read, a conversation at a time. Summaries are written by AI, for you only.">
      {problem && <Notice tone="crit">{problem}</Notice>}
      {rows.length === 0 ? (
        <p className="muted">You’re all caught up.</p>
      ) : (
        <ul className="pcatch-list">
          {rows.map((c) => (
            <li key={c.id} className="pcatch-item">
              <div className="pcatch-head">
                <Link to={`${base}/${c.id}`} className="pcatch-title">
                  {c.kind === 'channel' ? `#${c.title}` : c.title}
                </Link>
                <span className="muted">{plural(c.unread, 'new message')}</span>
                {!done[c.id] && (
                  <Button size="small" icon="spark" disabled={busy === c.id} onClick={() => void run(c)}>
                    {busy === c.id ? 'Writing…' : 'Catch me up'}
                  </Button>
                )}
              </div>
              {done[c.id] && (
                <CatchUpPanel
                  catchUp={done[c.id]!}
                  empty="Nothing new since you last read."
                  onClose={() =>
                    setDone((d) => {
                      const next = { ...d };
                      delete next[c.id];
                      return next;
                    })
                  }
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}

/** The live Patches, to go to their conversation (and patch in there). */
function PatchesSheet({ open, onClose, base, patches, titleOf }: { open: boolean; onClose: () => void; base: string; patches: PatchView[]; titleOf: (id: string) => string | null }) {
  return (
    <Sheet open={open} onClose={onClose} title="Patches" subtitle="Quick calls going on in your conversations. Open one to patch in.">
      {patches.length === 0 ? (
        <p className="muted">No Patches going on now. Start one from a channel or conversation: tap Patch in.</p>
      ) : (
        <div className="you-rows">
          {patches.map((p) => (
            <Link key={p.id} to={`${base}/${p.conversationId}`} className="you-row">
              <Icon name="headset" size={18} />
              <span className="grow cell-stack">
                <span>{titleOf(p.conversationId) ?? 'A conversation'}</span>
                <small className="muted">{p.people.map((x) => x.name).join(', ') || 'Starting'}</small>
              </span>
            </Link>
          ))}
        </div>
      )}
    </Sheet>
  );
}

/** Start something: a direct message, a channel (admins), or find a channel to join. */
function ComposeButton({ admin, onDialog, dmOnly, canText }: { admin: boolean; onDialog: PhoneInboxProps['onDialog']; dmOnly?: boolean; canText?: boolean }) {
  const [open, setOpen] = useState(false);
  const pick = (d: Parameters<PhoneInboxProps['onDialog']>[0]) => {
    setOpen(false);
    onDialog(d);
  };
  return (
    <>
      <button type="button" className="papp-fab" aria-label={dmOnly ? 'New message' : 'Start something new'} onClick={() => (dmOnly ? onDialog('dm') : setOpen(true))}>
        <Icon name="edit" size={22} strokeWidth={2} />
      </button>
      {!dmOnly && (
        <Sheet open={open} onClose={() => setOpen(false)} title="New">
          <div className="you-rows">
            <button type="button" className="you-row" onClick={() => pick('dm')}>
              <Icon name="chat" size={18} />
              Message someone
            </button>
            {canText && (
              <button type="button" className="you-row" onClick={() => pick('text')}>
                <Icon name="phone" size={18} />
                Text a phone number
              </button>
            )}
            {admin && (
              <button type="button" className="you-row" onClick={() => pick('channel')}>
                <Icon name="hash" size={18} />
                New channel
              </button>
            )}
            <button type="button" className="you-row" onClick={() => pick('browse')}>
              <Icon name="search" size={18} />
              Browse channels
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}

const byNewest = (a: ConversationView, b: ConversationView) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '');

export function PhoneHome(p: PhoneInboxProps) {
  useRememberList();
  const { domain, base, myId, presence } = p;
  const [sheet, setSheet] = useState<'' | 'catchup' | 'patches'>('');
  const [showArchived, setShowArchived] = useState(false);
  const live = useLivePatches(domain);
  const hasPhone = Boolean(p.inbox.me.extension);
  const voicemail = useNewVoicemail(domain, hasPhone);
  const t = tenantOf(base);
  const titleOf = (id: string) => {
    const c = p.inbox.conversations.find((x) => x.id === id);
    return c ? (c.kind === 'channel' ? `#${c.title}` : c.title) : null;
  };

  // Unread first, the way Slack does it: direct messages, then mentions, then everything else.
  const unreadDms = p.dms.filter((c) => c.unread > 0 && !muted(c)).sort(byNewest);
  // A team's texts show once, in the team's own section below, not again here (2984).
  const ownTexts = p.texts.filter((c) => !c.team);
  const mentioned = [...p.channels, ...ownTexts].filter((c) => c.mentions > 0).sort(byNewest);
  const unreads = [...p.channels, ...ownTexts].filter((c) => c.unread > 0 && c.mentions === 0 && !muted(c)).sort(byNewest);
  const shown = new Set([...unreadDms, ...mentioned, ...unreads].map((c) => c.id));
  const channels = p.channels.filter((c) => !shown.has(c.id));
  const dms = p.dms
    .filter((c) => !shown.has(c.id))
    .sort(byNewest)
    .slice(0, 8);
  const texts = p.texts.filter((c) => !shown.has(c.id) && !c.team).sort(byNewest);
  // Texts to a ring group's or queue's number: under the team (1.12.32), waiting ones first.
  const teamTexts = p.texts.filter((c) => c.team);
  const teams = [...new Map(teamTexts.map((c) => [c.team!.extension, c.team!])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const unreadTotal = [...unreadDms, ...mentioned, ...unreads].reduce((n, c) => n + c.unread, 0);
  const catchUpRows = [...unreadDms, ...mentioned, ...unreads].filter((c) => c.unread > 0);
  const allCaught = !unreadDms.length && !mentioned.length && !unreads.length;
  const mentionCount = p.inbox.conversations.reduce((n, c) => n + c.mentions, 0);

  return (
    <div className="phome with-fab">
      <PhonePageHead title="Home" hidden />
      <InstallOffer inline />
      <div className="phome-cards" role="list" aria-label="Shortcuts">
        {/* smart messaging: Catch up, where the company has it on */}
        <CatchUpCard total={unreadTotal} onClick={() => setSheet('catchup')} />
        <QuickCard icon="at" title="Mentions" sub={mentionCount ? `${mentionCount} new` : 'None new'} hot={mentionCount > 0} to={`${t}/feed`} />
        {live?.enabled && (
          <QuickCard icon="headset" title="Patches" sub={live.patches.length ? `${live.patches.length} live` : 'None live'} hot={live.patches.length > 0} onClick={() => setSheet('patches')} />
        )}
        {hasPhone && (
          <QuickCard
            icon="mail"
            title="Voicemail"
            sub={voicemail === null ? 'Your mailbox' : voicemail ? `${voicemail} new` : 'None new'}
            hot={Boolean(voicemail)}
            to={`${t}/me/calls?show=voicemail`}
          />
        )}
      </div>

      <nav className="phome-nav" aria-label="Conversations">
        {allCaught && (
          <p className="phome-caught">
            <Icon name="check" size={16} strokeWidth={2.4} />
            You’re all caught up.
          </p>
        )}
        {unreadDms.length > 0 && (
          <Section id="unread-dms" title="Unread DMs" icon="chat" to={`${t}/dms`}>
            {unreadDms.map((c) => (
              <Row key={c.id} base={base} c={c} myId={myId} presence={presence} count={c.unread} />
            ))}
          </Section>
        )}
        {mentioned.length > 0 && (
          <Section id="mentions" title="Mentions" icon="at" to={`${t}/feed`}>
            {mentioned.map((c) => (
              <Row key={c.id} base={base} c={c} myId={myId} presence={presence} count={c.mentions} countLabel={c.mentions === 1 ? 'mention' : 'mentions'} />
            ))}
          </Section>
        )}
        {unreads.length > 0 && (
          <Section id="unreads" title="Unreads" collapsible>
            {unreads.map((c) => (
              <Row key={c.id} base={base} c={c} myId={myId} presence={presence} count={c.kind === 'text' ? c.unread : undefined} />
            ))}
          </Section>
        )}
        <Section id="channels" title="Channels" collapsible>
          {channels.map((c) => (
            <Row key={c.id} base={base} c={c} myId={myId} presence={presence} />
          ))}
          <AddRow label={p.admin ? 'Add a channel' : 'Browse channels'} onClick={() => p.onDialog(p.admin ? 'channel' : 'browse')} />
        </Section>
        <Section id="dms" title="Direct messages" collapsible>
          {dms.map((c) => (
            <Row key={c.id} base={base} c={c} myId={myId} presence={presence} />
          ))}
          {p.dms.length > dms.length + unreadDms.length && (
            <Link to={`${t}/dms`} className="phome-row more">
              <span className="phome-row-icon" aria-hidden="true">
                <Icon name="chevRight" size={18} />
              </span>
              <span className="phome-row-title">All direct messages</span>
            </Link>
          )}
          <AddRow label="Message someone" onClick={() => p.onDialog('dm')} />
        </Section>
        {p.showTexts && (
          <Section id="texts" title="Texts" collapsible>
            {texts.map((c) => (
              <Row key={c.id} base={base} c={c} myId={myId} presence={presence} />
            ))}
            {!p.texts.length && <p className="phome-empty">Texts to {(p.inbox.texting.from?.length ?? 0) > 1 ? 'your numbers' : 'your number'} show here.</p>}
            {p.texts.length > 0 && !texts.length && !p.texts.some((c) => !c.team) && <p className="phome-empty">Your texts are under your teams below.</p>}
            {p.archivedTexts.length > 0 && (
              <button type="button" className="phome-row more" aria-expanded={showArchived} onClick={() => setShowArchived((v) => !v)}>
                <span className="phome-row-icon" aria-hidden="true">
                  <Icon name="archive" size={18} />
                </span>
                <span className="phome-row-title">
                  {showArchived ? 'Hide archived texts' : `Archived texts (${p.archivedTexts.length})`}
                </span>
              </button>
            )}
            {showArchived && p.archivedTexts.map((c) => <Row key={c.id} base={base} c={c} myId={myId} presence={presence} />)}
            {p.canStartText && <AddRow label="Text a phone number" onClick={() => p.onDialog('text')} />}
          </Section>
        )}
        {teams.map((t) => {
          const list = teamTexts.filter((c) => c.team!.extension === t.extension).sort(teamOrder);
          const out = t.kind === 'callQueue' && !t.signedIn && !t.supervisor;
          return (
            <Section key={t.extension} id={`team-${t.extension}`} title={`${t.name} ${t.kind === 'callQueue' ? 'queue' : 'ring group'}`} collapsible>
              {out && (
                <p className="phome-empty">
                  You’re signed out of {t.name}: its texts are read-only. <Link to={`/t/${encodeURIComponent(p.domain)}/me`}>Sign in</Link>
                </p>
              )}
              {list.map((c) => (
                <Row key={c.id} base={base} c={c} myId={myId} presence={presence} />
              ))}
            </Section>
          );
        })}
      </nav>
      {/* smart messaging: Spam, archived channels, Later, snoozed and Done */}
      <div className="phome-smart">
        <SmartSide domain={domain} base={base} conversations={p.inbox.conversations} admin={p.admin} onChanged={p.onChanged} />
      </div>

      <ComposeButton admin={p.admin} onDialog={p.onDialog} canText={p.canStartText} />
      <CatchUpSheet open={sheet === 'catchup'} onClose={() => setSheet('')} domain={domain} base={base} rows={catchUpRows} />
      <PatchesSheet open={sheet === 'patches'} onClose={() => setSheet('')} base={base} patches={live?.patches ?? []} titleOf={titleOf} />
    </div>
  );
}

/** Catch up, where the company has it on (smart messaging). */
function CatchUpCard({ total, onClick }: { total: number; onClick: () => void }) {
  const smart = useSmartOn();
  if (!smart) return null;
  return <QuickCard icon="spark" title="Catch up" sub={total ? `${total} new` : 'All caught up'} hot={total > 0} onClick={onClick} />;
}

// --- DMs ------------------------------------------------------------------------------------------

/** "You: see you at 3", "Dana: on my way" (in a group), "Sent a picture". Code shows as its words, without its marks. */
function previewText(c: ConversationView): string {
  const pv = c.preview;
  if (!pv) return 'No messages yet';
  const words = plainPreview(pv.body) || (pv.attached ? 'Sent a file' : '');
  if (pv.mine) return `You: ${words}`;
  const group = c.people.length > 2;
  return group && pv.author ? `${pv.author.split(' ')[0]}: ${words}` : words;
}

function DmRow({ base, c, myId, presence }: { base: string; c: ConversationView; myId: string; presence: PresenceList | undefined }) {
  const quiet = muted(c);
  const unread = quiet ? 0 : c.unread;
  const at = c.preview?.at ?? c.lastMessageAt;
  return (
    <li>
      <Link to={`${base}/${c.id}`} className={`pdm${unread ? ' unread' : ''}${quiet ? ' muted' : ''}`}>
        <span className="pdm-icon" aria-hidden="true">
          <ConversationIcon c={c} myId={myId} presence={presence} />
        </span>
        <span className="pdm-main">
          <span className="pdm-top">
            <strong className="pdm-name">{c.title || 'Just you'}</strong>
            {at && <span className="pdm-when">{shortWhen(at)}</span>}
          </span>
          <span className="pdm-bottom">
            <span className="pdm-preview" data-private="">
              {previewText(c)}
            </span>
            {unread > 0 && (
              <span className="phome-badge">
                {unread > 99 ? '99+' : unread}
                <span className="sr-only"> unread</span>
              </span>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

export function PhoneDms(p: PhoneInboxProps) {
  useRememberList();
  const [q, setQ] = useState('');
  const all = [...p.dms].sort(byNewest);
  const words = q.trim().toLowerCase();
  const list = words ? all.filter((c) => c.title.toLowerCase().includes(words)) : all;
  return (
    <div className="phome pdms with-fab">
      <PhonePageHead title="DMs" />
      {all.length > 6 && (
        <label className="search pdms-find">
          <Icon name="search" size={16} />
          <input type="search" placeholder="Find a person" aria-label="Find a direct message" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      )}
      {all.length === 0 ? (
        <Empty title="No direct messages yet">Message someone in {p.company}: tap the pencil.</Empty>
      ) : list.length === 0 ? (
        <p className="phome-empty">No one by that name.</p>
      ) : (
        <ul className="pdms-list">
          {list.map((c) => (
            <DmRow key={c.id} base={p.base} c={c} myId={p.myId} presence={p.presence} />
          ))}
        </ul>
      )}
      <ComposeButton admin={p.admin} onDialog={p.onDialog} dmOnly />
    </div>
  );
}

/** Catch up is on for the company (smart messaging). */
function useSmartOn(): boolean {
  return useContext(SmartContext).catchUpOn;
}
